import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectEditStatus, selectPageAttributes } from '../store/placementPageAttributes.selector';
import { selectEditableData } from 'store/redux-store/placement-details/selectors';
import { ActionAccessorHook } from './ActionAccessorHook';
import { InlineEditableField } from '@AMIEWEB/Common/EditPage/InlineEditableField';
import { CustomTextField } from '../CustomComponents/CustomTextField';
import { editableFields } from '../../editUtils';
import { pageAttributesActions } from '../store/placementPageAttributes.redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

export const ContractHours = ({ id }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { control } = useFormContext();
  const { contractHours: status } = useSelector(selectEditStatus);
  const { editOrderStack } = useSelector(selectPageAttributes);
  const { contractHours: defaultValue } = useSelector(selectEditableData);
  const isValidValue = /^[0-9]{1,4}$/;

  /** Code: useWatch has been used to retain value while avoidParentForm is triggered in Placement screen wrapper */
  const contractHours = useWatch<string>({ name: 'contractHours', control });

  const undoChecker = () => !editOrderStack.some(key => key === 'contractHours');
  return (
    <Controller
      control={control}
      name="contractHours"
      render={({ field: { onChange, ...rest } }) => (
        <Box sx={{ display: 'flex', width: 'fit-content' }}>
          {/** Component is used to access inaccessible methods from react-hook-form Controller
           * to invoke actions - say undo action */}
          <ActionAccessorHook
            actionChecker={undoChecker}
            accessAction={() => onChange(defaultValue)}
            hookDependancies={[editOrderStack]}
          />

          <InlineEditableField
            {...rest}
            id={id}
            label={contractHours}
            tooltipText={status.disabled ? t('placement.profile.edit.disableText') : ''}
            inputProps={{
              value: contractHours,
              minWidth: 80,
              maxLength: 4,
              renderedFromInlineEditor: true,
            }}
            Control={CustomTextField}
            onChange={newValue => {
              newValue = newValue ?? '';
              if (isValidValue.test(newValue) || newValue === '') {
                onChange(newValue);
                dispatch(pageAttributesActions.pushToEditOrderStack(editableFields.contractHours));
              }
            }}
            setCurrentFocus={state => dispatch(pageAttributesActions.setCurrentFocus(state !== null ? id : null))}
            {...status}
          />
        </Box>
      )}
    />
  );
};
